import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';

const blobToDataUrl = (blob: Blob): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve((reader.result as string) || '');
		reader.onerror = () => reject(reader.error || new Error('Unable to convert image to data URL'));
		reader.readAsDataURL(blob);
	});
};

const resolveImageToDataUrl = async (imageUrl: string): Promise<string> => {
	const response = await fetch(imageUrl, { mode: 'cors' });
	if (!response.ok) {
		throw new Error('Failed to load image for export');
	}
	const blob = await response.blob();
	return blobToDataUrl(blob);
};

const resolveAttachmentToDataUrl = async (
	image: Api.IFileAttachment,
	{ apiBaseUrl, accessToken }: { apiBaseUrl: string; accessToken?: string }
): Promise<string> => {
	if (image.id) {
		const normalizedBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
		const headers = new Headers();
		if (accessToken) {
			headers.set('Authorization', `bearer ${accessToken}`);
		}
		const response = await fetch(`${normalizedBaseUrl}/attachment/${image.id}/stream`, {
			headers,
			mode: 'cors',
			credentials: 'omit',
		});
		if (response.ok) {
			const responseContentType = response.headers.get('content-type') || '';
			if (!responseContentType.startsWith('image/')) {
				throw new Error('Attachment stream did not return an image content type');
			}

			const blob = await response.blob();
			if (!blob.size) {
				throw new Error('Attachment stream returned an empty image');
			}
			return blobToDataUrl(blob);
		}
	}

	if (image.url) {
		return resolveImageToDataUrl(image.url);
	}

	throw new Error('No image source available');
};

export const useGetImageUrl = () => {
	const userSession = useUserSession();

	const getImageUrl = useCallback(
		async (image: Api.IFileAttachment) => {
			try {
				const credentials = await userSession.webServiceHelper.getCredentialStore().getCredential();

				const dataUrl = await resolveAttachmentToDataUrl(image, {
					apiBaseUrl: userSession.webServiceHelper.baseUrl,
					accessToken: credentials?.access_token,
				});

				return dataUrl;
			} catch {
				throw new Error('Unable to prepare image for export. Please upload an image or use a template image.');
			}
		},
		[userSession]
	);

	return { getImageUrl };
};
